import React from "react";

import { Package } from "lucide-react";

import { Badge } from "~/components/ui/badge";
import { Separator } from "~/components/ui/separator";
import { formatCurrency } from "~/utils/helpers";
import { calcLineTotal } from "~/utils/lineItemCalculations";

export interface SalesRequestItem {
  id: string;
  productName: string;
  quantity: number;
  unitPrice: number;
  unit?: string;
  discountPercent?: number;
}

interface SalesRequestItemsListProps {
  items: SalesRequestItem[];
  paidAmount?: number;
  totalAmount?: number;
}

const SalesRequestItemsList: React.FC<SalesRequestItemsListProps> = ({
  items,
  paidAmount = 0,
  totalAmount = 0,
}) => {
  if (!items.length) {
    return (
      <div className="text-sm italic text-muted-foreground">
        No hay productos en esta nota de venta
      </div>
    );
  }

  const showPaymentStatus = totalAmount > 0;
  const isPaidFully = paidAmount >= totalAmount;
  const isPaidPartially = paidAmount > 0 && paidAmount < totalAmount;
  const remainingAmount = totalAmount - paidAmount;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1 text-sm text-muted-foreground">
        <Package className="h-4 w-4" />
        <span>Productos</span>
      </div>
      <Separator className="my-2" />
      <div className="space-y-2">
        {items.map((item) => (
          <div key={item.id} className="flex justify-between text-sm">
            <div className="flex items-center gap-1">
              <Badge variant="outline" className="font-normal">
                x{item.quantity} {item.unit || ""}
              </Badge>
              <span>{item.productName}</span>
            </div>
            <span className="font-medium">
              {formatCurrency(
                calcLineTotal(
                  item.unitPrice,
                  item.quantity,
                  item.discountPercent ?? 0
                )
              )}
            </span>
          </div>
        ))}
      </div>

      {showPaymentStatus && (
        <>
          <Separator className="my-2" />
          <div className="flex justify-between text-sm">
            <span>Estado de Pago:</span>
            <div className="flex flex-col items-end">
              {isPaidFully ? (
                <Badge className="bg-green-500 text-white">
                  Pagado Completamente
                </Badge>
              ) : isPaidPartially ? (
                <>
                  <Badge className="mb-1 bg-yellow-500 text-white">
                    Pago Parcial
                  </Badge>
                  <span className="text-xs">
                    Pagado: {formatCurrency(paidAmount)} de{" "}
                    {formatCurrency(totalAmount)}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    Pendiente: {formatCurrency(remainingAmount)}
                  </span>
                </>
              ) : (
                <Badge
                  variant="outline"
                  className="border-amber-500 text-amber-500"
                >
                  Pendiente
                </Badge>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SalesRequestItemsList;
